import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Card } from '../ui'
import type { ImpactReport } from '../../types'

interface Props {
  report: ImpactReport
  className?: string
  /** Rating scales top out at 5 everywhere the report is used today. */
  maxScore?: number
  /** Skip the Card wrapper when the caller already draws one (marketing hero). */
  bare?: boolean
}

/** Paired baseline and latest bars, one pair per question. */
const ImpactComparisonBars: React.FC<Props> = ({ report, className, maxScore = 5, bare }) => {
  const bars = (
    <div className="space-y-5">
      {report.per_question.map((q, i) => {
        const bPct = (q.baseline_avg / maxScore) * 100
        const lPct = (q.latest_avg / maxScore) * 100
        return (
          <div key={i}>
            <p className="text-sm font-medium text-foreground mb-2">{q.question_text}</p>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-muted w-14"><FormattedMessage id="impact.baseline" /></span>
                <div className="flex-1 bg-background-alt rounded-full h-4">
                  <div
                    className="bg-foreground-muted h-4 rounded-full transition-all"
                    style={{ width: `${Math.min(bPct, 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-foreground-muted w-8 text-right">
                  {q.baseline_avg.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-muted w-14"><FormattedMessage id="impact.latest" /></span>
                <div className="flex-1 bg-background-alt rounded-full h-4">
                  <div
                    className={`h-4 rounded-full transition-all ${
                      q.direction === 'improved' ? 'bg-success'
                      : q.direction === 'declined' ? 'bg-error'
                      : 'bg-foreground-muted'
                    }`}
                    style={{ width: `${Math.min(lPct, 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-foreground-muted w-8 text-right">
                  {q.latest_avg.toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  if (bare) return <div className={className}>{bars}</div>
  return <Card elevated className={className}>{bars}</Card>
}

export default ImpactComparisonBars
